import React, { useContext, useEffect, useRef, useState } from "react";
import { useQuery } from "@apollo/react-hooks";
import { ChoiceList, FormLayout, Select, TextField } from "@shopify/polaris";
import PropsType from "prop-types";

// import lib
import { baseHelper } from "lib/helpers";
import { useDebounce } from "lib/hooks";
import { PrivateContext } from "lib/context";
import constant from "lib/constant/constant";
import { GET_PRODUCT_TAGS, GET_PRODUCT_TYPE } from "app/productOld/apollo/queries";
import { GET_CATALOG } from "app/product/apollo/queries";

const PRODUCT_DIFF_FIELD_CHOICES = [
  { label: "Title", value: "title" },
  { label: "Description", value: "description" },
  { label: "Tags", value: "tags" },
  { label: "Product Type", value: "productType" },
  { label: "Price", value: "price" },
  { label: "Compare Price", value: "comparePrice" },
  { label: "SKU", value: "sku" },
  { label: "Barcode", value: "barcode" },
  { label: "Images", value: "images" },
  { label: "Weight", value: "weight" },
  { label: "Weight Unit", value: "weightUnit" },
  { label: "Quantity", value: "quantity" },
  { label: "Variants", value: "variants" },
  // { label: "Is Shipping", value: "isShipping" },
  // { label: "Is Taxable", value: "isTaxable" },
  { label: "Measurement", value: "measurement" },
  // { label: "Custom Metafields", value: "customMetafields" },
  // { label: "Attachments", value: "attachments" },
];

// The "Like" box drives a regex over the whole product collection, the same as
// the main search bar, so it waits for typing to settle instead of issuing a
// request per keystroke.
const LIKE_DEBOUNCE_MS = 600;

/**
 * The text input behind the "Like" advance filter. The field itself stays
 * uncontrolled by the parent so typing is instant; only the committed value is
 * pushed upwards, and only once typing stops.
 */
const LikeFilter = (props) => {
  const { label, value, onChange } = props;
  const [text, setText] = useState(value || "");
  const debouncedText = useDebounce(text, LIKE_DEBOUNCE_MS);
  const committed = useRef(value || "");

  // A change from outside - "Clear", "Clear all filters", a different filter
  // being picked - replaces what is typed without waiting for the debounce.
  useEffect(() => {
    const next = value || "";
    if (next !== committed.current) {
      committed.current = next;
      setText(next);
    }
  }, [value]);

  useEffect(() => {
    if (debouncedText !== committed.current) {
      committed.current = debouncedText;
      onChange(debouncedText);
    }
    // onChange is rebuilt on every render of the parent; depending on it here
    // would re-fire the push on unrelated renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedText]);

  return <TextField label={label} value={text} onChange={setText} />;
};

LikeFilter.defaultProps = {
  label: "",
  value: "",
  onChange: () => {},
};

LikeFilter.propTypes = {
  label: PropsType.string,
  value: PropsType.string,
  onChange: PropsType.func,
};

const AdvanceFilters = (props) => {
  const { dataToFetch, setDataToFetch, setTaggedWith, taggedWith, selectedFilter, productDiffSelected, onProductDiffChange, productDiffCounts } = props;
  const { cms, history } = useContext(PrivateContext);

  const { data: getProductTagsData } = useQuery(GET_PRODUCT_TAGS);
  const { data: getProductTypeData } = useQuery(GET_PRODUCT_TYPE);
  const { data: catalogData } = useQuery(GET_CATALOG);

  const filterTag = baseHelper.getResponseData(getProductTagsData, constant.gql.GET_PRODUCT_TAGS) || [];
  const filterType = baseHelper.getResponseData(getProductTypeData, constant.gql.GET_PRODUCT_TYPE) || [];
  const productCatalogOption = baseHelper.getResponseData(catalogData, constant.gql.GET_CATALOG) || [];

  const typeArray =
    (filterType &&
      filterType.length &&
      filterType.map((item) => {
        return item.label;
      })) ||
    [];

  const collectionsArray = productCatalogOption?.catalog
    ? productCatalogOption.catalog.map((item) => {
        const { title = "", storeCatalogId = 0 } = item || {};
        const optionData = {
          value: title,
          label: title,
          id: storeCatalogId.toFixed(1).toString(),
        };
        return optionData;
      })
    : [];

  const filterStatus = [
    { label: cms("label.new"), value: cms("label.new").toLowerCase() },
    { label: cms("label.review"), value: cms("label.review").toLowerCase() },
    { label: cms("label.approve"), value: cms("label.approve").toLowerCase() },
    { label: cms("label.reject"), value: cms("label.reject").toLowerCase() },
    { label: cms("label.delete"), value: cms("label.delete").toLowerCase() },
  ];

  const handleSelectChange = (value) => {
    setTaggedWith(value);
    baseHelper.setUrl(history, { filter: value });
  };

  const handleChange = (value) => {
    setTaggedWith(value);
    let updatedData = {};
    if (selectedFilter === constant.COLLECTION) {
      const labelset = collectionsArray.filter((item) => {
        return item.value === value;
      });
      const setCatId = (labelset && labelset.length && labelset[0].id) || "";
      updatedData = { list_cat_id: setCatId };
    }

    const listSearch = baseHelper.setQueryParams(history.location, { list_search: value, ...updatedData });
    history.push(`${history.location.pathname}?${listSearch}`);
    const filterApplied = baseHelper.queryParamsFromLocation(history);
    setDataToFetch({ ...dataToFetch, ...filterApplied, ...updatedData });
  };

  return (
    <>
      <FormLayout>
        {(selectedFilter && selectedFilter === constant.TAG.toLowerCase() && (
          <Select
            label={baseHelper.ucFirst(cms("label.Select"))}
            onChange={handleChange}
            value={taggedWith}
            placeholder={cms("label.selectTag")}
            options={filterTag.tags}
          />
        )) ||
          (selectedFilter && selectedFilter === constant.TYPE && (
            <Select
              onChange={handleChange}
              value={taggedWith}
              label={baseHelper.ucFirst(cms("label.Select"))}
              placeholder={cms("label.selectType")}
              options={typeArray}
            />
          )) ||
          (selectedFilter && selectedFilter === constant.COLLECTION && (
            <Select
              onChange={handleChange}
              value={taggedWith}
              label={baseHelper.ucFirst(cms("label.Select"))}
              placeholder={cms("label.selectCollection")}
              options={collectionsArray}
            />
          )) ||
          (selectedFilter && selectedFilter === constant.PRODUCT_DIFF && (
            <ProductDiffFilter
              selected={productDiffSelected}
              onChange={onProductDiffChange}
              counts={productDiffCounts}
            />
          )) ||
          (selectedFilter && selectedFilter !== constant.STATUS && selectedFilter !== constant.PRODUCT_DIFF && (
            <LikeFilter label={cms("common.label.like")} value={taggedWith} onChange={handleChange} />
          )) ||
          (selectedFilter === constant.STATUS && (
            <Select
              label={baseHelper.ucFirst(cms("common.label.status"))}
              placeholder={cms("common.label.select")}
              onChange={handleSelectChange}
              options={filterStatus}
            />
          )) ||
          null}
      </FormLayout>
    </>
  );
};

const ProductDiffFilter = (props) => {
  const { selected, onChange, counts } = props;
  const selectedValues = Array.isArray(selected) ? selected : [];

  const countMap = (counts || []).reduce((acc, { key, count }) => {
    acc[key] = count;
    return acc;
  }, {});

  const choices = PRODUCT_DIFF_FIELD_CHOICES.map(({ label, value }) => {
    const count = countMap[value];
    return {
      label: count ? `${label} (${count})` : label,
      value,
    };
  });

  return (
    <ChoiceList
      title="Filter by Review Fields"
      titleHidden
      choices={choices}
      selected={selectedValues}
      onChange={(val) => onChange(val || [])}
      allowMultiple
    />
  );
};

ProductDiffFilter.defaultProps = {
  selected: [],
  onChange: () => {},
  counts: [],
};

ProductDiffFilter.propTypes = {
  selected: PropsType.arrayOf(PropsType.string),
  onChange: PropsType.func,
  counts  : PropsType.arrayOf(PropsType.shape({ key: PropsType.string, count: PropsType.number })),
};

const ShopifyStatusFilter = (props) => {
  const { selected, onChange } = props;
  const { cms } = useContext(PrivateContext);

  const choices = [
    { label: "Active", value: "active" },
    { label: "Draft", value: "draft" },
    { label: "Archived", value: "archived" },
    { label: "Unlisted", value: "unlisted" },
    { label: cms("label.synced"), value: constant.SYNCED },
    { label: cms("label.notSynced"), value: constant.NOT_SYNCED },
    { label: cms("label.outOfStock") || "Out of stock", value: constant.OUT_OF_STOCK },
    { label: cms("label.inStock") || "In stock", value: constant.IN_STOCK },
  ];

  const selectedValues = Array.isArray(selected) ? selected : [];

  return (
    <ChoiceList
      title={cms("common.label.filterByShopifyStatus") || "Filter By Shopify Status"}
      titleHidden
      choices={choices}
      selected={selectedValues}
      onChange={(val) => onChange(val || [])}
      allowMultiple
    />
  );
};

ShopifyStatusFilter.defaultProps = {
  selected: [],
  onChange: () => {},
};

ShopifyStatusFilter.propTypes = {
  selected: PropsType.arrayOf(PropsType.string),
  onChange: PropsType.func,
};

const StockFilter = (props) => {
  const { selected, onChange } = props;
  const { cms } = useContext(PrivateContext);

  const choices = [
    { label: cms("label.outOfStock") || "Out of stock", value: constant.OUT_OF_STOCK },
    { label: cms("label.inStock") || "In stock", value: constant.IN_STOCK },
  ];

  const selectedValues = Array.isArray(selected) ? selected : [];

  return (
    <ChoiceList
      title={cms("common.label.filterByStock") || "Filter by stock"}
      titleHidden
      choices={choices}
      selected={selectedValues}
      onChange={(val) => onChange(val || [])}
      allowMultiple
    />
  );
};

StockFilter.defaultProps = {
  selected: [],
  onChange: () => {},
};

StockFilter.propTypes = {
  selected: PropsType.arrayOf(PropsType.string),
  onChange: PropsType.func,
};

const Filter = (props) => {
  const {
    dataToFetch,
    isLiveConnect = false,
    productList,
    selectedFilter,
    setDataToFetch,
    setSelectedFilter,
    setTaggedWith,
    vendorProductListData,
  } = props;
  const { cms, history, currentUser } = useContext(PrivateContext);

  const filterOptions = isLiveConnect
    ? [{ label: cms("common.label.titleName"), value: constant.TITLES }]
    : [
        { label: cms("common.label.titleName"), value: constant.TITLE },
        { label: cms("label.tag"), value: constant.TAG.toLowerCase() },
        { label: cms("common.label.type"), value: constant.TYPE },
        { label: cms("label.collection"), value: constant.COLLECTION },
        { label: cms("common.label.sku"), value: constant.SKU },
      ];

  const isVendor = baseHelper.isVendor(currentUser);

  if (!isVendor) {
    filterOptions.push({ label: cms("common.label.vendor"), value: constant.VENDOR });
    filterOptions.push({ label: "Review Fields", value: constant.PRODUCT_DIFF });
  }
  if (productList || vendorProductListData) {
    filterOptions.push({ label: cms("label.platform"), value: constant.LIVECONNECTED_PLATFORM });
  }
  const handleSelectChange = (value) => {
    let paramValue = value;
    if (value === constant.TITLES) {
      paramValue = constant.TITLE;
    }
    setSelectedFilter(value);
    let listFilter = baseHelper.setQueryParams(history.location, { list_filter: paramValue });
    if (value !== constant.STATUS) {
      setTaggedWith("");
      listFilter = baseHelper.setQueryParams(history.location, { list_filter: paramValue, list_search: "" });
    }
    history.push(`${history.location.pathname}?${listFilter}`);
    const filterApplied = baseHelper.queryParamsFromLocation(history);

    setDataToFetch({ ...dataToFetch, ...filterApplied });
  };
  return (
    <Select
      label={cms("common.label.showAll")}
      placeholder={cms("common.placeholder.select")}
      value={selectedFilter || ""}
      onChange={handleSelectChange}
      options={filterOptions}
    />
  );
};

AdvanceFilters.defaultProps = {
  dataToFetch: {},
  setDataToFetch: () => {},
  setTaggedWith: () => {},
  taggedWith: "",
  selectedFilter: "",
  productDiffSelected: [],
  onProductDiffChange: () => {},
};

AdvanceFilters.propTypes = {
  dataToFetch: PropsType.shape(PropsType.object),
  setDataToFetch: PropsType.func,
  setTaggedWith: PropsType.func,
  taggedWith: PropsType.string,
  selectedFilter: PropsType.string,
  productDiffSelected: PropsType.arrayOf(PropsType.string),
  onProductDiffChange: PropsType.func,
};

Filter.defaultProps = {
  dataToFetch: {},
  setDataToFetch: () => {},
  setTaggedWith: () => {},
  setSelectedFilter: () => {},
  selectedFilter: "",
};

Filter.propTypes = {
  dataToFetch: PropsType.shape(PropsType.object),
  setDataToFetch: PropsType.func,
  setTaggedWith: PropsType.func,
  setSelectedFilter: PropsType.func,
  selectedFilter: PropsType.string,
};

export { AdvanceFilters as default, Filter, LikeFilter, ProductDiffFilter, ShopifyStatusFilter, StockFilter, PRODUCT_DIFF_FIELD_CHOICES };
